import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';

export interface BreathingExercise {
  id: 'relax' | 'focus' | 'unwind' | 'energize';
}

const EXERCISE_GRADIENTS: Record<string, string> = {
  relax: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  focus: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  unwind: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  energize: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
};

const EXERCISE_DEEPLINKS: Record<string, string> = {
  relax: 'meditopia://startbreathexercise',
  focus: 'meditopia://startbreathexercise',
  unwind: 'meditopia://startbreathexercise',
  energize: 'meditopia://startbreathexercise',
};

const Card = styled.div<{ $gradient: string }>`
  width: 155px;
  height: 120px;
  border-radius: 14px;
  background: ${({ $gradient }) => $gradient};
  padding: 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform ${theme.transitions.fast};
  position: relative;
  overflow: hidden;

  &:active {
    transform: scale(0.96);
  }

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    right: -20px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: var(--color-border-subtle);
  }
`;

const ExerciseName = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: #fff;
  line-height: 1.2;
`;

const Pattern = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeight.regular};
  color: var(--color-text);
  line-height: 1.3;
`;

const StartButton = styled.div`
  align-self: flex-start;
  padding: 5px 14px;
  border-radius: 20px;
  background: var(--color-border);
  backdrop-filter: blur(4px);
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: #fff;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: var(--color-text-muted);
  }
`;

interface BreathingExerciseCardProps {
  exercise: BreathingExercise;
}

export const BreathingExerciseCard: React.FC<BreathingExerciseCardProps> = ({ exercise }) => {
  const { t } = useTranslation();
  const gradient = EXERCISE_GRADIENTS[exercise.id] || EXERCISE_GRADIENTS.relax;
  const deeplink = EXERCISE_DEEPLINKS[exercise.id] || EXERCISE_DEEPLINKS.relax;

  const handleClick = () => {
    window.open(deeplink, '_blank');
  };

  return (
    <Card $gradient={gradient} onClick={handleClick}>
      <div>
        <ExerciseName>{t(`breathing.${exercise.id}`)}</ExerciseName>
        <Pattern>{t(`breathing.${exercise.id}Pattern`)}</Pattern>
      </div>
      <StartButton>{t('breathing.start')}</StartButton>
    </Card>
  );
};
